import { IDatasetConfig, DatasetActionOpenType, ListViewItemTypes, FieldViews, IPaginationResponse } from 'app/interfaces';
import { fieldsMap, TYPE_CUSTOM } from './fields';
import generalForm from './forms/general.form';

/**
 * Catalogo dei metodi di incasso del dominio: famiglie standard (definite dalla piattaforma,
 * rinominabili no, abilitabili sì) e metodi custom creati dal cliente per gli incassi offline.
 * Alimenta la mappatura Server RT e la dichiarazione dell'incasso in back-office.
 *
 * Virtual dataset: le righe non sono record di tabella, l'id è il codice del metodo.
 * Le azioni per riga arrivano dal server (allowed_actions): le famiglie non hanno delete.
 */

/**
 * Avviso quando la dichiarazione dell'incasso è obbligatoria ma nessun metodo è selezionabile:
 * la conferma da back-office resterebbe bloccata senza opzioni.
 */
function getMissingBackofficeMethodsReview(paginationResponse: IPaginationResponse<any>, metaReview: any): string{
	const meta = paginationResponse.meta || {};
	if(!meta[metaReview]) return '';
	return '<span class="red-fg">La dichiarazione dell\'incasso è obbligatoria ma nessun metodo è'
		+ ' selezionabile in back-office: attivane almeno uno, altrimenti le prenotazioni non'
		+ ' possono essere confermate.</span>';
}

export const PaymentMethodCatalogConfig: IDatasetConfig = {
	name: 'payment_method_catalog',
	title: 'Metodi di incasso',
	singleTitle: 'Metodo di incasso',
	ajaxDatasetCode: 'payment_method_catalog',
	docLink: '/docs/touroperators/cantability/rt-payment-method-catalog.php',
	fieldsMap: fieldsMap,
	enabledActions: {
		detail: true,
		create: true,
		edit: true,
		delete: true,
		list: true
	},
	formConfigsMap: {
		generalForm: generalForm
	},
	defaultActions: {
		detail: {
			openType: DatasetActionOpenType.DIALOG,
			beforeCalls: [
				{
					name: 'record',
					path: '/dataset/payment_method_catalog/detail/:recordId'
				}
			],
			viewConfig: {
				formConfig: generalForm,
				actions: {
					edit: true,
					delete: true
				}
			}
		},
		create: {
			openType: DatasetActionOpenType.DIALOG,
			viewConfig: {
				formConfig: generalForm
			}
		},
		edit: {
			openType: DatasetActionOpenType.DIALOG,
			viewConfig: {
				formConfig: generalForm
			}
		},
		list: {
			openType: DatasetActionOpenType.PAGE,
			viewConfig: {
				itemViewType: ListViewItemTypes.TABLE,
				showSearchBar: true,
				columns: [
					{
						title: 'Metodo',
						key: 'label',
						fieldView: FieldViews.HTML,
						getDisplayHTML(record: any): string{
							return '<b>' + (record.label || '') + '</b><br><small class="secondary-text">'
								+ (record.code || '') + '</small>';
						}
					},
					{
						title: 'Tipo',
						key: 'type',
						fieldView: FieldViews.HTML,
						getDisplayHTML(record: any): string{
							if(record.type === TYPE_CUSTOM) return 'Personalizzato';
							return '<span class="secondary-text">Standard</span>';
						}
					},
					{
						title: 'Selezionabile in back-office',
						key: 'backoffice_enabled',
						fieldView: FieldViews.HTML,
						getDisplayHTML(record: any): string{
							if(!record.backoffice_enabled){
								return '<span class="secondary-text">No</span>';
							}
							return '<span class="material-icons-outlined green-fg"'
								+ ' style="vertical-align:middle;font-size:18px;">check_circle</span> Sì';
						}
					},
					{
						title: 'Mappatura Server RT',
						key: 'mapping_missing',
						fieldView: FieldViews.HTML,
						getDisplayHTML(record: any): string{
							if(record.has_mapping) return 'Mappato';
							// stesso avviso della schermata Mappatura pagamenti: senza mappatura il
							// documento parte con il codice di ripiego, che su un metodo dichiarato
							// allo sportello è quasi sempre sbagliato
							if(record.mapping_missing){
								return '<span class="material-icons-outlined red-fg"'
									+ ' style="vertical-align:middle;font-size:18px;"'
									+ ' title="Senza mappatura il documento usa il codice di ripiego">warning</span>'
									+ ' <span class="red-fg">Non mappato</span>';
							}
							// famiglia non dichiarabile allo sportello e non mappata: niente da dire,
							// scrivere "Mappato" sarebbe falso
							return '<span class="secondary-text">&mdash;</span>';
						}
					}
				],
				actions: ['create', 'detail', 'edit', 'delete'],
				metaReviews: ['backoffice_methods_missing'],
				getMetaReview: getMissingBackofficeMethodsReview
			}
		}
	}
};
